/**
 * Список особового складу на звільнення: з постійного складу та подій дня
 * виходить готовий список рядків.
 *
 * Правила перенесені з окремої програми «Список на звільнення» без змін —
 * розклад, пріоритети та сортування ті самі, бо документ підписують саме
 * в такому вигляді. Модуль чистий: жодного доступу до бази чи мережі,
 * тож ті самі правила рахують і на сервері, і в браузері.
 *
 * У людини немає постійних ознак на кшталт «у наряді» чи «без звільнення».
 * Є склад, який живе завжди, і події, які живуть один день.
 */
import { ukKey } from './people.js';
import { DEFAULT_LEAVE_SCHEDULE } from './defaults.js';
/** «19:10» -> ['19', '10']. Порожнє чи криве — за загальним розпорядком. */
function asHM(time) {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time ?? '');
    return m ? [m[1], m[2]] : null;
}
/**
 * Години групи для цієї людини: спершу ті, що задані її шаблону харчування,
 * потім спільні.
 */
function unitHours(day, pattern) {
    const own = pattern ? day?.byPattern?.[pattern] : undefined;
    return {
        from: asHM(own?.from || day?.from),
        to: asHM(own?.to || day?.to),
    };
}
export const MODE_LABEL = {
    weekday: 'будній день',
    saturday: 'субота',
    sunday: 'неділя',
};
/**
 * Розпорядок для конкретної людини: якщо її шаблон харчування має власні
 * години, вони лягають поверх загальних — у тій частині, що відповідає її
 * життю. Інакше розпорядок дня лишається як є.
 */
function scheduleFor(sc, p) {
    const own = p.pattern ? sc.byPattern?.[p.pattern] : undefined;
    if (!own)
        return sc;
    const life = p.category === 'report' ? p.base ?? 'kyiv' : p.category;
    if (life === 'kyiv') {
        const k = sc.kyiv ?? { from: null };
        return {
            ...sc,
            ...(own.to ? { toMorning: own.to } : {}),
            kyiv: {
                ...k,
                ...(own.day ? { day: own.day } : {}),
                ...(own.from ? { from: own.from } : {}),
                ...(own.goodFrom ? { goodFrom: own.goodFrom } : {}),
                ...(own.goodFrom && own.goodTo ? { goodTo: own.goodTo } : {}),
            },
        };
    }
    return {
        ...sc,
        ...(own.day ? { day: own.day } : {}),
        ...(own.from ? { from: own.from } : {}),
        ...(own.to ? { toRegular: own.to } : {}),
        ...(own.goodFrom ? { goodFrom: own.goodFrom } : {}),
        ...(own.goodFrom && own.goodTo ? { goodTo: own.goodTo } : {}),
    };
}
/**
 * Розпорядок одного типу дня з налаштувань. Порожня година — «немає»: типовою
 * її не підміняємо, інакше в налаштуваннях видно одне, а в списку інше.
 */
function scheduleDay(day) {
    const dayFrom = asHM(day.dayFrom);
    const dayTo = asHM(day.dayTo);
    const dutyFrom = asHM(day.beforeDutyFrom);
    const dutyTo = asHM(day.beforeDutyTo);
    const out = {
        from: asHM(day.from),
        toRegular: asHM(day.toRegular),
        toMorning: asHM(day.toMorning),
    };
    if (dayFrom && dayTo)
        out.day = [dayFrom, dayTo];
    const handover = asHM(day.handoverFrom);
    if (handover)
        out.handoverFrom = handover;
    if (dutyFrom && dutyTo)
        out.beforeDuty = [dutyFrom, dutyTo];
    const report = asHM(day.reportFrom);
    if (report)
        out.reportFrom = report;
    if (day.reportOvernight)
        out.reportOvernight = true;
    const good = asHM(day.goodFrom);
    if (good)
        out.goodFrom = good;
    const goodTo = asHM(day.goodTo);
    if (good && goodTo)
        out.goodTo = goodTo;
    const kyivDayFrom = asHM(day.kyivDayFrom);
    const kyivDayTo = asHM(day.kyivDayTo);
    const kyivGoodFrom = asHM(day.kyivGoodFrom);
    const kyivGoodTo = asHM(day.kyivGoodTo);
    out.kyiv = {
        from: asHM(day.kyivFrom),
        ...(kyivDayFrom && kyivDayTo ? { day: [kyivDayFrom, kyivDayTo] } : {}),
        ...(kyivGoodFrom ? { goodFrom: kyivGoodFrom } : {}),
        ...(kyivGoodFrom && kyivGoodTo ? { goodTo: kyivGoodTo } : {}),
    };
    const patterns = {};
    for (const [id, h] of Object.entries(day.byPattern ?? {})) {
        const f = asHM(h.dayFrom);
        const t = asHM(h.dayTo);
        const from = asHM(h.from);
        const to = asHM(h.to);
        const gf = asHM(h.goodFrom);
        const gt = asHM(h.goodTo);
        const own = {
            ...(f && t ? { day: [f, t] } : {}),
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
            ...(gf ? { goodFrom: gf } : {}),
            ...(gf && gt ? { goodTo: gt } : {}),
        };
        if (Object.keys(own).length)
            patterns[id] = own;
    }
    if (Object.keys(patterns).length)
        out.byPattern = patterns;
    return out;
}
/** Розпорядок усіх типів дня; без налаштувань — типовий. */
export function scheduleOf(settings) {
    const d = DEFAULT_LEAVE_SCHEDULE;
    const s = settings ?? d;
    return {
        weekday: scheduleDay(s.weekday ?? d.weekday),
        saturday: scheduleDay(s.saturday ?? d.saturday),
        sunday: scheduleDay(s.sunday ?? d.sunday),
    };
}
export const SCHEDULE = scheduleOf();
/** Суббота и воскресенье по дате. Праздники переключаются руками. */
export function modeOf(iso) {
    const d = new Date(iso + 'T00:00:00Z').getUTCDay();
    return d === 6 ? 'saturday' : d === 0 ? 'sunday' : 'weekday';
}
/**
 * Причины, по которым человека нет в списке. Порядок = приоритет:
 * закрытое увольнение перебивает всё остальное, поэтому оно первое.
 * Наряд идёт после — «заступает» отменяет увольнение, но если
 * увольнение уже закрыто, в примечании должна остаться настоящая причина.
 */
const BLOCKERS = [
    { kind: 'blocked', label: 'звільнення закрито' },
    { kind: 'duty_on', label: 'заступає в наряд' },
    { kind: 'absent', label: 'відсутній' },
];
// КБ тут нет намеренно: звільнення он получает как все и попадает
// в список. В розрахунку его показывают отдельной строкой — справочно,
// в «Н» он не считается и в подсчёт не входит.
export const KIND_LABEL = {
    blocked: 'звільнення закрито',
    duty_on: 'заступає в наряд',
    duty_off: 'здає наряд',
    duty_kb: 'КБ',
    absent: 'відсутній',
    custom: 'свій час',
};
/** «07:00-11:00» → отрезок на этот день. Конец раньше начала — до утра. */
export function parseSpan(text, day, next) {
    const m = text.trim().match(/^(\d{1,2})[:.](\d{2})\s*[-–—]\s*(\d{1,2})[:.](\d{2})$/);
    if (!m)
        return null;
    const [, fh, fm, th, tm] = m;
    const pad = (v) => v.padStart(2, '0');
    const overnight = minutes(fh, fm) >= minutes(th, tm);
    return {
        fromH: pad(fh), fromM: fm, fromDay: shortDay(day),
        toH: pad(th), toM: tm, toDay: shortDay(overnight ? next : day),
    };
}
const CATEGORY_RANK = { report: 0, kyiv: 1, regular: 2 };
/**
 * У тих, хто здав наряд, порядок зворотний: спершу іногородні,
 * бо вони повертаються о 21:00, а вже потім ті, хто до ранку.
 */
const AFTER_RANK = { regular: 0, kyiv: 1, report: 2 };
/**
 * Старшинство звань. Усередині групи спершу сержанти, потім солдати —
 * так список і читають. Порядок важливий: шукаємо за початком рядка,
 * щоб «ст. с-т» не збігся раніше за «с-т».
 */
const RANK_ORDER = [
    'гол. с-т', 'головний сержант',
    'ст. с-т', 'старший сержант',
    'сержант', 'с-т',
    'мол. с-т', 'молодший сержант',
    'солдат',
];
function rankByName(rank) {
    const r = rank.toLowerCase().replace(/\s+/g, ' ').trim();
    const i = RANK_ORDER.findIndex((x) => r.startsWith(x));
    // Незнайоме звання — до солдатів, але після них
    return i < 0 ? RANK_ORDER.length : i;
}
/**
 * Місце людини за старшинством. Довідник звань знає його точно, тож коли
 * старшинство передане — беремо його; назви лишаються запасним шляхом для
 * викликів без довідника.
 */
function rankRank(person) {
    return person.seniority ?? rankByName(person.rank);
}
/**
 * Коли людина виходить: початок першого відрізка, у хвилинах від півночі.
 *
 * За цим списком і сортують, і відбивають рискою: різний час — різні рядки
 * для того, хто читає аркуш.
 */
export function leaveStartOf(r) {
    const first = r.spans[0];
    return first ? minutes(first.fromH, first.fromM) : 0;
}
export const bandOf = (r) => r.person.category === 'report' ? 'report'
    : r.custom ? 'custom'
        : r.beforeDuty ? 'before'
            : r.afterDuty ? 'after'
                : r.person.category;
/**
 * Кому положено звільнення до ранку.
 *
 * Рапорт сам по собі цього не вирішує: у будній день людина з рапортом
 * лишається до ранку, лише якщо вона киянин. А от у суботу за рапортом
 * до ранку йдуть усі — це задано в розкладі окремо.
 */
function overnight(p, sc) {
    if (p.category === 'report') {
        return sc.reportOvernight ?? (p.base ?? 'kyiv') === 'kyiv';
    }
    return p.category === 'kyiv';
}
/* ── час ── */
export const DUTY_CHANGE = '18:00';
/** КБ зайнятий до 17:00 — його відрізки зсуваються вперед. */
export const KB_UNTIL = '17:00';
const minutes = (h, m) => Number(h) * 60 + Number(m);
const atMinutes = (hhmm) => {
    const [h, m] = hhmm.split(':');
    return minutes(h, m);
};
/* ── порядок за азбукою живе поруч з іменами: див. `ukKey` у people.ts ── */
function cmpArr(a, b) {
    const n = Math.min(a.length, b.length);
    for (let i = 0; i < n; i++)
        if (a[i] !== b[i])
            return a[i] - b[i];
    return a.length - b.length;
}
/* ── собственно правила ── */
function spansFor(p, afterDuty, kb, beforeDuty, mode, iso, isoNext, group = { from: null, to: null }, schedule = SCHEDULE) {
    // В документе даты короткие — «25.08.», без года
    const day = shortDay(iso);
    const next = shortDay(isoNext);
    const sc = scheduleFor(schedule[mode], p);
    // Заступає в наряд сьогодні — встигає лише короткий відрізок до нього
    if (beforeDuty && sc.beforeDuty) {
        const [f, t] = sc.beforeDuty;
        return [{ fromH: f[0], fromM: f[1], fromDay: day, toH: t[0], toM: t[1], toDay: day }];
    }
    const stay = overnight(p, sc);
    const to = stay ? sc.toMorning : sc.toRegular;
    const toDay = stay ? next : day;
    // Наряд сдают в 18:00 — раньше человека просто нет на месте.
    // В выходные звільнення начинается раньше смены, поэтому сдающему
    // отрезок отсчитывается от смены, а не от общего начала.
    const handover = sc.handoverFrom ?? DUTY_CHANGE.split(':');
    /*
     * Що задає початок звільнення, від старшого до молодшого:
     *
     *   рапорт -> позначка людини -> розпорядок групи -> розклад дня.
     *
     * Рапорт і позначка — це про саму людину: наряд вона здає о своїй годині,
     * кібер наряд несе до своєї, і коли б не виходила решта групи, її час від
     * цього не міняється. Без налаштування КБ лишається на давній 17:00 — саме
     * її звіряли з розпорядком.
     */
    const reportFrom = p.category === 'report' ? sc.reportFrom : undefined;
    const markFrom = asHM(p.markFrom) ?? (kb ? asHM(KB_UNTIL) : null);
    /*
     * Перенесення групи не знімає людину зі зміни. Хто здає наряд сьогодні,
     * раніше за зміну не виходить: перенесений на 17:15 вихід групи його не
     * звільняє о 17:15, бо до 18:00 він ще в наряді. Пізніша година групи діє
     * як завжди — тоді разом з усіма.
     */
    const groupFrom = afterDuty && group.from && atMinutes(hm(group.from)) < atMinutes(hm(handover))
        ? handover
        : group.from;
    // Хорошист виходить зі своєї години щодня, але лише коли група йде за
    // звичним розпорядком. Позначка дня (наряд, КБ) і перенесення групи старші
    // за нього: це причина саме цього дня, і хорошист іде разом з групою.
    // Хто здав наряд, до зміни ще в наряді — година хорошиста його не відпускає.
    // Хорошист-киянин має свою годину, коли її задано: кияни ввечері виходять
    // раніше, тож і денний відрізок у них свій.
    const life = p.category === 'report' ? p.base ?? 'kyiv' : p.category;
    const kv = life === 'kyiv' ? sc.kyiv : undefined;
    const good = kv?.goodFrom ? { from: kv.goodFrom, to: kv.goodTo } : { from: sc.goodFrom, to: sc.goodTo };
    const goodFrom = p.good && !afterDuty ? good.from ?? null : null;
    const own = reportFrom ? null : markFrom ?? groupFrom ?? goodFrom;
    /*
     * Своя година посуває початок першого відрізка — і тільки його. Решта
     * розпорядку та сама: повертаються так само, і ввечері виходять так само.
     * Тобто «15:15–18:30; 19:10–07:30» стає «18:00–18:30; 19:10–07:30».
     *
     * Денний відрізок є лише в будні. Хто здав наряд, вільної людини немає до
     * зміни — тому денний починається зі зміни (18:00–18:30), а не зникає.
     * Година, не раніша за кінець денного відрізка, означає, що денного
     * цього дня просто немає: посунути початок за кінець не можна, тож першим
     * стає вечірній.
     */
    // Кияни виходять за власним розпорядком — він і задає денний відрізок та
    // початок вечірнього; іногородні й решта — за загальним.
    // Хорошист із власним кінцем має свій денний відрізок замість загального —
    // так іногородній хорошист вільний 15:15–18:30, хоч решті в будні нічого.
    const goodRange = goodFrom && own === goodFrom && good.to ? [goodFrom, good.to] : null;
    const dayRange = goodRange ?? (kv ? kv.day : sc.day);
    const eveningFrom = kv ? kv.from : sc.from;
    const dayEnd = dayRange ? atMinutes(hm(dayRange[1])) : 0;
    const dayStart = own ?? (afterDuty ? handover : dayRange?.[0] ?? null);
    const daySpan = dayRange && dayStart && atMinutes(hm(dayStart)) < dayEnd
        ? {
            fromH: dayStart[0], fromM: dayStart[1], fromDay: day,
            toH: dayRange[1][0], toM: dayRange[1][1], toDay: day,
        }
        : null;
    // Основне звільнення без початку чи без повернення в розпорядку — «немає»:
    // тоді лишається хіба денний відрізок.
    const start = reportFrom ?? eveningFrom;
    if (!start || !to)
        return daySpan ? [daySpan] : [];
    // За рапортом відпускають раніше — але не раніше, ніж здав наряд. Коли
    // годину задано самій позначці наряду, вона про ту здачу вже все й сказала:
    // тоді зміна за розкладом не рахується, інакше поставлену годину не зсунути
    // раніше за неї.
    const base = reportFrom ?? (daySpan ? start : own ?? start);
    const from = !markFrom && afterDuty && atMinutes(hm(base)) < atMinutes(hm(handover))
        ? handover
        : base;
    /*
     * «До» групи посуває кінець останнього відрізка. Через північ він переходить
     * за тим самим правилом, що й решта відрізків: кінець не пізніший за початок
     * означає ранок наступного дня.
     */
    const back = group.to ?? to;
    const backDay = group.to
        ? (atMinutes(hm(group.to)) <= atMinutes(hm(from)) ? next : day)
        : toDay;
    const evening = {
        fromH: from[0], fromM: from[1], fromDay: day,
        toH: back[0], toM: back[1], toDay: backDay,
    };
    if (!daySpan)
        return [evening];
    // Вечірній починається раніше, ніж закінчився денний, — це одне звільнення.
    // Кінець хорошиста задано окремо — встик до вечірнього він лишається своїм
    // відрізком: «15:15–18:00; 18:00–07:30», як пишуть у бланку.
    const fromMin = atMinutes(hm(from));
    if (goodRange ? fromMin < dayEnd : fromMin <= dayEnd) {
        return [{ ...daySpan, toH: evening.toH, toM: evening.toM, toDay: evening.toDay }];
    }
    return [daySpan, evening];
}
const hm = (t) => `${t[0]}:${t[1]}`;
/**
 * Ранок за рапортом (лікарня, військкомат) закінчується до загального виходу —
 * тоді це додаток до розпорядку, а не заміна. «09:00–13:00» + будні
 * «15:15–18:30; 19:10–…». Якщо відрізок накриває сам вихід (14:30–21:00),
 * він лишається єдиним: клерк уже сказав увесь період.
 */
function withCustom(custom, regular) {
    const first = regular[0];
    if (!first)
        return [custom];
    const extra = custom.toDay === custom.fromDay &&
        custom.fromDay === first.fromDay &&
        minutes(custom.toH, custom.toM) < minutes(first.fromH, first.fromM);
    return extra ? [custom, ...regular] : [custom];
}
/** YYYY-MM-DD → DD.MM. */
export const shortDay = (iso) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
/** YYYY-MM-DD → DD.MM.YYYY */
export const longDay = (iso) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
export function nextDay(iso) {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
}
/**
 * ЗАКРЫТО → НАРЯД → СДАЧА НАРЯДА → КАТЕГОРИЯ → СОРТИРОВКА.
 *
 * Люди из состава не пропадают: кого нет в списке, тот попадает
 * в skipped с причиной — её видно в предпросмотре.
 */
export function buildPlan(people, events, day, mode = modeOf(day), units = {}, schedule = SCHEDULE) {
    const byPerson = new Map();
    const reasons = new Map();
    const roles = new Map();
    const ownSpans = new Map();
    for (const e of events) {
        let set = byPerson.get(e.personId);
        if (!set)
            byPerson.set(e.personId, (set = new Set()));
        set.add(e.kind);
        if (e.reason)
            reasons.set(`${e.personId}:${e.kind}`, e.reason);
        if (e.role)
            roles.set(`${e.personId}:${e.kind}`, e.role);
        if (e.span)
            ownSpans.set(e.personId, e.span);
    }
    const next = nextDay(day);
    const rows = [];
    const skipped = [];
    for (const person of people) {
        const kinds = byPerson.get(person.id) ?? new Set();
        // В неділю заступаючим звільнення все одно належить — коротким
        // відрізком до заступлення, тому в список вони потрапляють
        const beforeDuty = kinds.has('duty_on') && !!schedule[mode].beforeDuty;
        const blocker = BLOCKERS.find((b) => kinds.has(b.kind) && !(b.kind === 'duty_on' && beforeDuty));
        if (blocker) {
            skipped.push({
                person,
                kind: blocker.kind,
                label: blocker.label,
                reason: reasons.get(`${person.id}:${blocker.kind}`),
                afterDuty: kinds.has('duty_off'),
                role: roles.get(`${person.id}:${blocker.kind}`) ?? roles.get(`${person.id}:duty_off`),
            });
            continue;
        }
        const afterDuty = kinds.has('duty_off');
        // Групі цього дня звільнення не належить. Стоїть після подій навмисне:
        // «заступає в наряд» чи «відсутній» — причина точніша, і в примітці має
        // лишитися саме вона, а розпорядок групи береться, коли інших причин немає.
        const group = units[person.grp];
        if (group && !group.on) {
            skipped.push({
                person,
                kind: 'blocked',
                label: 'звільнення групі не належить',
                afterDuty,
                role: roles.get(`${person.id}:duty_off`),
            });
            continue;
        }
        // Ранок до загального виходу додається до розпорядку; відрізок, що
        // накриває сам вихід, замінює його цілком.
        const own = ownSpans.get(person.id);
        const custom = own ? parseSpan(own, day, next) : null;
        const regular = spansFor(person, afterDuty, kinds.has('duty_kb'), beforeDuty, mode, day, next, unitHours(group, person.pattern), schedule);
        // Розпорядок цього дня людині нічого не дає — і свого часу в неї немає.
        if (!regular.length && !custom) {
            skipped.push({
                person,
                kind: 'blocked',
                label: 'звільнення за розпорядком немає',
                afterDuty,
                role: roles.get(`${person.id}:duty_off`),
            });
            continue;
        }
        rows.push({
            person, afterDuty, custom: !!custom,
            spans: custom ? withCustom(custom, regular) : regular,
            role: roles.get(`${person.id}:duty_off`) ?? roles.get(`${person.id}:duty_on`)
                ?? roles.get(`${person.id}:duty_kb`),
            kb: kinds.has('duty_kb'),
            beforeDuty,
        });
    }
    /**
     * Аркуш читають зверху вниз за годиною виходу. Рапорт — окремий блок
     * над усіма, всередині за групою й прізвищем, не за своїм ранком.
     * Заступаючі в неділю теж окремо: у них той самий початок, що в решти,
     * але найкоротший відрізок, тож їх шукають першими.
     */
    const top = (r) => r.person.category === 'report' ? 0 : r.beforeDuty ? 1 : 2;
    const inBand = (a, b) => {
        const report = a.person.category === 'report' && b.person.category === 'report';
        if (report) {
            return CATEGORY_RANK[a.person.category] - CATEGORY_RANK[b.person.category] ||
                a.person.grp.localeCompare(b.person.grp) ||
                rankRank(a.person) - rankRank(b.person);
        }
        if (a.beforeDuty && b.beforeDuty) {
            return leaveStartOf(a) - leaveStartOf(b) ||
                rankRank(a.person) - rankRank(b.person) ||
                a.person.grp.localeCompare(b.person.grp);
        }
        // Хто здав наряд — окремим блоком унизу: їх година залежить від зміни,
        // і першими там ідуть іногородні, бо вони повертаються о 21:00.
        if (a.afterDuty !== b.afterDuty)
            return Number(a.afterDuty) - Number(b.afterDuty);
        if (a.afterDuty && b.afterDuty) {
            return leaveStartOf(a) - leaveStartOf(b) ||
                AFTER_RANK[a.person.category] - AFTER_RANK[b.person.category] ||
                rankRank(a.person) - rankRank(b.person) ||
                a.person.grp.localeCompare(b.person.grp);
        }
        // Решта: спершу всі кияни, потім усі іногородні — навіть коли киян
        // відпустили пізніше (перенесли пару). Усередині — за годиною виходу.
        return CATEGORY_RANK[a.person.category] - CATEGORY_RANK[b.person.category] ||
            leaveStartOf(a) - leaveStartOf(b) ||
            a.person.grp.localeCompare(b.person.grp) ||
            rankRank(a.person) - rankRank(b.person);
    };
    rows.sort((a, b) => top(a) - top(b) ||
        inBand(a, b) ||
        cmpArr(ukKey(a.person.surname), ukKey(b.person.surname)) ||
        a.person.initials.localeCompare(b.person.initials));
    skipped.sort((a, b) => BLOCKERS.findIndex((x) => x.kind === a.kind) - BLOCKERS.findIndex((x) => x.kind === b.kind) ||
        a.person.grp.localeCompare(b.person.grp) ||
        rankRank(a.person) - rankRank(b.person) ||
        cmpArr(ukKey(a.person.surname), ukKey(b.person.surname)));
    return { day, mode, rows, skipped };
}
//# sourceMappingURL=leave-list.js.map